import * as React from 'react';
import { Box, Typography, Container, alpha } from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi';

const categories = [
  { label: 'Trending', gradient: 'linear-gradient(135deg, #8B5CF6, #7C3AED)', emoji: '🔥' },
  { label: 'Action', gradient: 'linear-gradient(135deg, #EF4444, #DC2626)', emoji: '⚡' },
  { label: 'Romance', gradient: 'linear-gradient(135deg, #EC4899, #DB2777)', emoji: '💕' },
  { label: 'Fantasy', gradient: 'linear-gradient(135deg, #8B5CF6, #06B6D4)', emoji: '✨' },
  { label: 'Comedy', gradient: 'linear-gradient(135deg, #F59E0B, #D97706)', emoji: '😂' },
  { label: 'Horror', gradient: 'linear-gradient(135deg, #111827, #374151)', emoji: '👻' },
  { label: 'Sci-Fi', gradient: 'linear-gradient(135deg, #00B4D8, #0090B0)', emoji: '🚀' },
  { label: 'Slice of Life', gradient: 'linear-gradient(135deg, #10B981, #059669)', emoji: '🌸' },
];

export default function ShowcaseSection() {
  const navigate = useNavigate();

  return (
    <Box sx={{ position: 'relative', py: { xs: 10, md: 16 }, background: '#050505' }}>
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              sx={{
                display: 'inline-flex',
                px: 2,
                py: 0.5,
                borderRadius: 100,
                fontSize: '0.8rem',
                fontWeight: 600,
                color: '#00B4D8',
                background: 'rgba(0,180,216,0.1)',
                border: '1px solid rgba(0,180,216,0.2)',
                mb: 2,
              }}
            >
              Explore Genres
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                fontWeight: 800,
                letterSpacing: '-0.02em',
              }}
            >
              Find Your{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #EC4899, #8B5CF6)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                genre
              </Box>
            </Typography>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: 'repeat(4, 1fr)' },
            gap: { xs: 2, md: 3 },
          }}
        >
          {categories.map((cat, i) => (
            <motion.div
              key={cat.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <Box
                onClick={() => navigate(`/browse?genre=${encodeURIComponent(cat.label)}`)}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 4,
                  background: cat.gradient,
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 140,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                  '&:hover': {
                    transform: 'translateY(-4px) scale(1.02)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(to top, rgba(0,0,0,0.4) 0%, transparent 60%)',
                  },
                }}
              >
                <Typography sx={{ fontSize: '2.5rem', position: 'relative', zIndex: 1, mb: 1 }}>
                  {cat.emoji}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#fff',
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  {cat.label}
                </Typography>
              </Box>
            </motion.div>
          ))}
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Box sx={{ textAlign: 'center', mt: 5 }}>
            <Box
              onClick={() => navigate('/browse')}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1.5,
                color: '#8B5CF6',
                fontWeight: 600,
                cursor: 'pointer',
                fontSize: '1rem',
                transition: 'all 0.3s',
                '&:hover': { gap: 2, color: '#9C6CF7' },
              }}
            >
              Browse full library
              <HiArrowRight size={18} />
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
}
